import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { SelectWithSearch } from '../SelectWithSearch'
import { Tooltip } from '../Tooltip'

import countryAndNationalityList from '../../mocks/countryAndNationalityList'

type CountrySelectorProps = {
  label?: string
  value: string
  onValueChange: () => void
  isValid?: boolean
  errorMessage?: string
  placeholderMessageId: string
  tooltipMessageId?: string
  isNationalityList?: boolean
}

export const CountrySelector = ({
  label,
  value,
  onValueChange,
  isValid,
  errorMessage,
  placeholderMessageId,
  tooltipMessageId,
  isNationalityList = false,
}: CountrySelectorProps) => {
  const { formatMessage, locale } = useIntl()

  const options = useMemo(
    () =>
      countryAndNationalityList
        .map(
          ({
            num_code,
            country_en,
            country_es,
            country_pt,
            nationality_en,
            nationality_es,
            nationality_pt,
            alpha_3_code,
          }) => {
            if (isNationalityList) {
              let nationality
              switch (locale) {
                case 'es':
                  nationality = nationality_es
                  break
                case 'pt':
                  nationality = nationality_pt
                  break
                case 'en':
                default:
                  nationality = nationality_en
                  break
              }

              return { id: num_code, label: nationality, value: alpha_3_code }
            } else {
              let country
              switch (locale) {
                case 'es':
                  country = country_es
                  break
                case 'pt':
                  country = country_pt
                  break
                case 'en':
                default:
                  country = country_en
                  break
              }

              return { id: num_code, label: country, value: alpha_3_code }
            }
          }
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    [countryAndNationalityList, locale]
  )

  return (
    <div className="flex flex-col">
      {label && (
        <label className="flex items-center gap-2 mb-4 text-sm lg:text-sm text-grey-800 font-medium">
          {label}
          {tooltipMessageId && (
            <Tooltip>{formatMessage({ id: tooltipMessageId })}</Tooltip>
          )}
        </label>
      )}

      <SelectWithSearch
        value={value}
        onValueChange={onValueChange}
        defaultValue={value}
        options={options}
        searchPlaceholder={formatMessage({ id: placeholderMessageId })}
        noButton
        placeholder={formatMessage({ id: placeholderMessageId })}
      />

      {errorMessage && (
        <p className="mt-2 text-[#FF4949] text-xs">{errorMessage}</p>
      )}
    </div>
  )
}
